import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setLoading, loading } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }

        if (mounted) {
          if (session) {
            setUser(session.user);
            // Only redirect to dashboard if we're on an auth page
            if (location.pathname.startsWith('/auth/')) {
              navigate('/dashboard');
            }
          } else {
            setUser(null);
            // Only redirect to login if we're not on an auth page
            if (!location.pathname.startsWith('/auth/')) {
              navigate('/auth/login');
            }
          }
        }
      } catch (error) {
        console.error('Error in session check:', error);
        
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(checkSession, retryDelay * Math.pow(2, retryCount));
          return;
        }

        if (mounted) {
          toast({
            variant: 'destructive',
            title: 'Erro de conexão',
            description: 'Não foi possível verificar sua sessão. Tente novamente.',
          });
          setUser(null);
          if (!location.pathname.startsWith('/auth/')) {
            navigate('/auth/login');
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Initial session check
    checkSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setUser(session.user);
        if (location.pathname.startsWith('/auth/')) {
          navigate('/dashboard');
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        if (!location.pathname.startsWith('/auth/')) {
          navigate('/auth/login');
        }
      } else if (event === 'TOKEN_REFRESHED' && session) {
        setUser(session.user);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, setUser, setLoading, location.pathname]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return <>{children}</>;
}